import { NavLink, useLocation } from "react-router-dom"
import { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { useSidebarStore } from "@/stores/sidebar-store"
import { cn } from "@/lib/utils"
import {
  LayoutDashboard,
  Kanban,
  Building2,
  Users,
  CheckSquare,
  Calendar,
  Mail,
  MessageSquare,
  FileText,
  Receipt,
  Package,
  Wrench,
  Shield,
  FolderOpen,
  BarChart3,
  Target,
  GraduationCap,
  Zap,
  Webhook,
  Settings,
  PanelLeftClose,
  PanelLeft,
  Sparkles,
  ChevronRight,
  ChevronDown,
} from "lucide-react"

interface NavItem {
  icon: React.ElementType
  labelKey: string
  href: string
  dotColor?: string
}

const mainNav: NavItem[] = [
  { icon: LayoutDashboard, labelKey: "nav.dashboard", href: "/dashboard" },
  { icon: Sparkles, labelKey: "nav.ai", href: "/ai" },
  { icon: Kanban, labelKey: "nav.pipeline", href: "/deals", dotColor: "#EF4444" },
  { icon: Building2, labelKey: "nav.companies", href: "/companies", dotColor: "#3B82F6" },
  { icon: Users, labelKey: "nav.contacts", href: "/contacts", dotColor: "#22C55E" },
  { icon: CheckSquare, labelKey: "nav.tasks", href: "/tasks" },
  { icon: Calendar, labelKey: "nav.calendar", href: "/calendar" },
  { icon: Mail, labelKey: "nav.email", href: "/mail" },
  { icon: MessageSquare, labelKey: "nav.sms", href: "/sms" },
]

const docsNav: NavItem[] = [
  { icon: FileText, labelKey: "nav.quotes", href: "/quotes" },
  { icon: Receipt, labelKey: "nav.invoices", href: "/invoices" },
  { icon: Package, labelKey: "nav.products", href: "/products" },
  { icon: Wrench, labelKey: "nav.service", href: "/service" },
  { icon: Shield, labelKey: "nav.equipment", href: "/equipment" },
  { icon: FolderOpen, labelKey: "nav.documents", href: "/documents" },
]

const analyticsNav: NavItem[] = [
  { icon: BarChart3, labelKey: "nav.reports", href: "/reports" },
  { icon: Target, labelKey: "nav.goals", href: "/goals" },
  { icon: GraduationCap, labelKey: "nav.training", href: "/training" },
]

const systemNav: NavItem[] = [
  { icon: Zap, labelKey: "nav.automations", href: "/automations" },
  { icon: Webhook, labelKey: "nav.webhooks", href: "/webhooks" },
  { icon: Settings, labelKey: "nav.settings", href: "/settings" },
]

function NavGroup({
  items,
  collapsed,
}: {
  items: NavItem[]
  collapsed: boolean
}) {
  const { t } = useTranslation()
  const location = useLocation()

  return (
    <div className="flex flex-col gap-px">
      {items.map((item) => {
        const isActive = location.pathname.startsWith(item.href)
        const Icon = item.icon

        return (
          <NavLink
            key={item.href}
            to={item.href}
            className={cn(
              "flex h-7 items-center gap-1.5 rounded-[9px] py-0 pl-2 pr-1 text-[14px] font-medium tracking-[-0.01em] transition-colors",
              collapsed ? "justify-center px-2" : "",
              isActive
                ? "bg-[var(--sidebar-bg-active)] text-[var(--sidebar-text-active)]"
                : "text-[var(--sidebar-text)] hover:bg-[var(--sidebar-bg-hover)]",
            )}
            title={collapsed ? t(item.labelKey) : undefined}
          >
            <Icon size={14} strokeWidth={1.8} className="shrink-0 opacity-80" />
            {!collapsed && (
              <>
                <span className="truncate">{t(item.labelKey)}</span>
                {item.dotColor && (
                  <span
                    className="ml-auto h-3.5 w-3.5 shrink-0 rounded-[30%]"
                    style={{ backgroundColor: item.dotColor }}
                  />
                )}
              </>
            )}
          </NavLink>
        )
      })}
    </div>
  )
}

function CollapsibleSection({
  sectionId,
  label,
  items,
  sidebarCollapsed,
}: {
  sectionId: string
  label: string
  items: NavItem[]
  sidebarCollapsed: boolean
}) {
  const { collapsedSections, toggleSection } = useSidebarStore()
  const isOpen = !collapsedSections[sectionId]

  if (sidebarCollapsed) {
    return <NavGroup items={items} collapsed />
  }

  return (
    <div className="flex flex-col gap-0.5">
      <button
        onClick={() => toggleSection(sectionId)}
        className="flex h-4 items-center gap-1 px-2 text-[12px] font-medium leading-4 tracking-[-0.02em] text-black/55 transition-colors hover:text-black/70 dark:text-white/55 dark:hover:text-white/70"
      >
        {isOpen ? (
          <ChevronDown size={10} className="shrink-0" />
        ) : (
          <ChevronRight size={10} className="shrink-0" />
        )}
        {label}
      </button>
      {isOpen && <NavGroup items={items} collapsed={false} />}
    </div>
  )
}

export function Sidebar() {
  const { isCollapsed, isMobileOpen, setMobileOpen, toggle } = useSidebarStore()
  const { t } = useTranslation()
  const location = useLocation()

  // Close mobile sidebar on navigation
  useEffect(() => {
    setMobileOpen(false)
  }, [location.pathname, setMobileOpen])

  return (
    <>
      {/* Mobile backdrop */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/30 md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <aside
        className={cn(
          "fixed left-0 top-0 z-50 flex h-screen flex-col overflow-y-auto overflow-x-hidden bg-[var(--sidebar-bg)] transition-all duration-150 ease-out",
          // Mobile: off-screen by default, slide in when open
          "max-md:w-[260px]",
          isMobileOpen ? "max-md:translate-x-0" : "max-md:-translate-x-full",
          // Desktop: width based on collapsed state
          isCollapsed ? "md:w-[60px]" : "md:w-[260px]",
        )}
      >
        {/* Workspace header */}
        <div
          className={cn(
            "flex h-12 items-center px-3",
            isCollapsed ? "md:justify-center" : "gap-2"
          )}
        >
          <div
            className="flex h-6 w-6 items-center justify-center rounded-md bg-primary text-[10px] font-bold text-primary-foreground"
          >
            O
          </div>
          <span className={cn(
            "text-[16px] font-semibold tracking-[-0.02em] text-[var(--sidebar-text-active)]",
            isCollapsed ? "md:hidden" : ""
          )}>
            OkeyCRM
          </span>
          {!isCollapsed && (
            <ChevronDown size={12} className="ml-auto text-[var(--text-tertiary)]" />
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 space-y-3 px-2 py-1">
          <NavGroup items={mainNav} collapsed={isCollapsed} />
          <CollapsibleSection
            sectionId="docs"
            label={t("nav.documents_group", "Dokumenty")}
            items={docsNav}
            sidebarCollapsed={isCollapsed}
          />
          <CollapsibleSection
            sectionId="analytics"
            label={t("nav.analytics_group", "Analityka")}
            items={analyticsNav}
            sidebarCollapsed={isCollapsed}
          />
          <CollapsibleSection
            sectionId="system"
            label={t("nav.system_group", "System")}
            items={systemNav}
            sidebarCollapsed={isCollapsed}
          />
        </nav>

        {/* Collapse toggle (desktop only) */}
        <div className="hidden border-t px-2 py-1.5 md:block">
          <button
            onClick={toggle}
            className={cn(
              "flex h-7 items-center gap-1.5 rounded-[9px] px-2 text-[13px] font-medium text-[var(--text-muted)] transition-colors hover:bg-[var(--sidebar-bg-hover)] hover:text-[var(--sidebar-text-active)]",
              isCollapsed ? "justify-center w-full" : "w-full",
            )}
            aria-label={isCollapsed ? "Rozwiń menu" : "Zwiń menu"}
          >
            {isCollapsed ? <PanelLeft size={14} /> : <PanelLeftClose size={14} />}
            {!isCollapsed && <span className="truncate">Zwiń</span>}
          </button>
        </div>
      </aside>

      {/* Sidebar separator (Attio-style 1px line, not border) */}
      <div
        className="fixed top-0 z-50 hidden h-full w-px bg-[var(--sidebar-bg-active)] md:block max-md:hidden"
        style={{ left: isCollapsed ? 60 : 260 }}
      />
    </>
  )
}
